<template>
    <form @submit.prevent="requestUpdate">
        <b-form-group
            label="Status"
            label-cols="4"
            label-align="left"
        >
            <multiselect
                v-model="createForm.candidateStatus"
                track-by="value"
                label="text"
                placeholder="Select"
                select-label="Select"
                selected-label="Selected"
                deselect-label="Select"
                :options="candidateStatusDdl"
                :searchable="true"
                :allow-empty="false"
                class="cursor-pointer"
                :class="[submitted && !$v.createForm.candidateStatus.required ? 'border border-danger' : '']"
                v-on:input="getCandidateStatusData"
            />
            <div v-if="submitted && !$v.createForm.candidateStatus.required" class="invalid-feedback d-block text-right">
                {{ $t("message.requiredX", [$t("label.status")]) }}
            </div>
        </b-form-group>
        <template v-if="createForm.candidateStatus && candidateStatusDetailStatus">
            <b-form-group
                label="Remark"
                label-cols="4"
                label-align="left"
            >
                <b-form-textarea
                    v-model.trim="createForm.remark"
                />
            </b-form-group>
            <b-form-group
                label="Send email"
                label-cols="4"
                label-align="left"
            >
                <b-form-checkbox
                    v-model="createForm.isEmail"
                    switch
                    class="mt-2"
                />
            </b-form-group>
            <div
                v-if="createForm.isEmail"
                class="fade-in"
            >
                <b-form-group
                    label="Email Content"
                    label-cols="4"
                    label-align="left"
                >
                    <div :class="{ 'border border-danger': submitted && !$v.createForm.emailContent.required }">
                        <ckeditor
                            v-model="createForm.emailContent"
                            :editor="editor"
                            :config="editorConfig"
                        />
                    </div>
                    <div v-if="submitted && !$v.createForm.emailContent.required" class="text-danger small text-right">
                        {{ $t('message.requiredX', ['Email Content']) }}
                    </div>
                </b-form-group>
                <b-form-group
                    label="Email Subject"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.emailSubject"
                        :class="{ 'is-invalid': submitted && !$v.createForm.emailSubject.required }"
                        autocomplete="off"
                    />
                    <b-form-invalid-feedback v-if="submitted && !$v.createForm.emailSubject.required" class="text-right">
                        {{ $t('message.requiredX', ['Email Subject']) }}
                    </b-form-invalid-feedback>
                </b-form-group>
                <b-form-group
                    label="Email CC"
                    label-cols="4"
                    label-align="left"
                >
                    <multiselect
                        v-model="createForm.emailCc"
                        :options="[]"
                        :multiple="true"
                        :taggable="true"
                        :placeholder="$t('message.pressEnterToAddX', [])"
                        :tag-placeholder="$t('message.pressEnterToAddX', ['Email'])"
                        :show-options="false"
                        :show-no-options="false"
                        v-on:tag="addEmailCc"
                    />
                </b-form-group>
            </div>
        </template>
        <div
            v-else-if="createForm.candidateStatus"
            class="d-flex justify-content-center align-items-center m-5 p-5"
        >
            <b-spinner class="m-5" variant="info" role="status"></b-spinner>
        </div>
        <hr class="my-3 mx-n4">
        <div class="d-flex justify-content-end w-100">
            <b-button
                type="button"
                variant="danger"
                class="mr-2 col-2"
                @click="$emit('done')"
            >
                {{ $t('label.cancel') }}
            </b-button>
            <b-button
                type="submit"
                variant="info"
                class="col-2"
                :disabled="!status.save || !candidateStatusDetailStatus"
            >
                <span v-if="status.save">
                    {{ $t('label.updateX', []) }}
                </span>
                <div v-else>
                    <b-spinner small />
                </div>
            </b-button>
        </div>
    </form>
</template>
<script>
import CKEditor from '@ckeditor/ckeditor5-vue';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import Multiselect from "vue-multiselect";
import i18n from "@/i18n";
import { alertHelper } from "@/utils";
import { required, requiredIf } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { candidateMethods, candidateStatusMethods, lookupMethods } from "@/state/api";
import { CANDIDATE_MODULE } from "@/state/api/candidate.state";

export default {
    props: {
        candidateUUID: {
            type: String,
            required: true,
        },
        searchCriteria: {
            type: Object,
            required: false,
        },
    },
    validations() {
        return {
            createForm: {
                candidateStatus: { required },
                emailContent: {
                    required: requiredIf(function () { return this.createForm.isEmail; }),
                },
                emailSubject: {
                    required: requiredIf(function () { return this.createForm.isEmail; }),
                },
            },
        };
    },
    components: { Multiselect, ckeditor: CKEditor.component },
    created() {
        this.lookupCandidateStatus();
    },
    data() {
        return {
            submitted: false,
            createForm: this.defaultForm(),
            editor: ClassicEditor,
            editorConfig: {
                removePlugins: [
                    'ImageCaption'
                ],
                toolbar: [
                    'selectAll',
                    'heading',
                    'bold',
                    'italic',
                    'blockQuote',
                    'bulletedList',
                    'numberedList',
                    'undo',
                    'redo',
                    'outdent',
                    'indent',
                ],
            },
        };
    },
    methods: {
        ...candidateMethods,
        ...candidateStatusMethods,
        ...lookupMethods,
        defaultForm() {
            return {
                candidateStatus: null,
                remark: "",
                emailContent: "",
                emailSubject: "",
                emailCc: [],
                isEmail: false,
            };
        },
        getCandidateStatusData() {
            if (this.createForm.candidateStatus?.value) {
                this.getCandidateStatus({ uuid: this.createForm.candidateStatus.value })
                    .then((result) => {
                        if (result?.success) {
                            this.createForm = {
                                candidateStatus: this.createForm.candidateStatus,
                                remark: "",
                                emailContent: this.candidateStatusData.emailContent || "",
                                emailSubject: this.candidateStatusData.emailSubject || "",
                                emailCc: this.candidateStatusData.emailCc ?? [],
                                isEmail: this.candidateStatusData.isEmail ? true : false,
                            };
                        }
                    });
            }
        },
        requestUpdate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    uuid: this.candidateUUID,
                    candidateStatusUuid: this.createForm.candidateStatus?.value,
                    isEmail: this.createForm.isEmail ? 1 : 0,
                    remark: this.createForm.remark,
                    emailContent: this.createForm.emailContent || "",
                    emailSubject: this.createForm.emailSubject,
                    emailCc: this.createForm.emailCc,
                };
                this.updateCandidateApplication({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit('done');
                        }
                    }));
            }
        },
        addEmailCc(newTag) {
            const tag = newTag.trim();
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (tag && emailRegex.test(tag) && !this.createForm.emailCc.includes(tag)) this.createForm.emailCc.push(tag);
            else alertHelper.alertWarning({ message: "Invalid email", title: i18n.t('message.warning') });
        },
        resetForm() {
            this.createForm = this.defaultForm();
            this.submitted = false;
            this.$v.$reset();
        },
    },
    computed: {
        ...mapState(CANDIDATE_MODULE, ['status']),
        candidateStatusDdl() {
            return this.$store.state.api.lookup.candidateStatusDdl || [];
        },
        candidateStatusData() {
            return this.$store.state.api.candidateStatus.details;
        },
        candidateStatusDetailStatus() {
            return this.$store.state.api.candidateStatus.status.detail;
        },
    },
}
</script>
